import { Delete } from "@mui/icons-material";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldDefinitionAPI } from "../../../../../api";
import { useAlert } from "../../../../../lib/alert";
import { validateAddress } from "../../../../../model/Address";
import { useProcessAnimationContext } from "../../../../process/components/AnimationContext";
import ConfirmationModal from "../../../../process/components/confirmation-modal/ConfirmationModal";
import { useProcessExecutionContext } from "../../../../process/hooks";
import { taskEditingAtom } from "../../../hooks/useTaskExecutionEditingState";
import {
  ButtonGroupFormElement,
  CopilotAutofillElement,
  FormDefinitionType,
  FormElementType,
  TaskExecutionState,
  TaskForm,
  TaskFormExecution,
  TaskViews,
  TypographyFormElement,
} from "../../../types";
import { CopilotAutofillFormComponent } from "../../form-builder/copilot-autofill/CopilotAutofillFormComponent";
import { DEFAULT_RENDER } from "../TaskBody";
import { executeWizardFormTask } from "./executeFormTask";
import getCommonFormExecutionComponent from "./FormCommonExecutionComponents";
import { FormExecutionComponentProps } from "./FormExecutionComponent";
import {
  ButtonGroupFunction,
  ButtonGroupSettings,
  TaskButtonGroupFormComponent,
} from "./wizard-form-elements/TaskButtonGroupFormComponent";
import { TaskSummaryFormComponent } from "./wizard-form-elements/TaskSummaryFormComponent";

const STR_EDITING_STEP = "You are currently editing this step.";
const STR_CLEAR = "Clear";
const STR_SAVE_RETURN = "Save & Return";
const STR_PLEASE_COMPLETE =
  "Please complete all required fields before proceeding.";
const STR_INVALID_STEP = "Invalid step index.";

const WizardFormExecutionComponent = ({
  children = DEFAULT_RENDER,
  onExecute,
  ...props
}: FormExecutionComponentProps) => {
  const { error } = useAlert();
  const anim = useProcessAnimationContext();
  const { executeTask } = useProcessExecutionContext();
  const [editing, setEditing] = useAtom(taskEditingAtom);
  const [reviewMode, setReviewMode] = useState(false);
  const [returnTo, setReturnTo] = useState(0);

  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);

  // cast input data so TS compiler doesn't bark
  const propsTask = props.task as TaskForm;
  const propsExecution = props.taskExecution as TaskFormExecution | undefined;

  useEffect(() => {
    if (editing === undefined) {
      setEditing(propsExecution?.state !== TaskExecutionState.Completed);
    }
  }, [editing, propsExecution?.state, setEditing]);

  const trueElements = useMemo(() => {
    return props.elements ?? propsTask.data?.form?.elements ?? [];
  }, [props.elements, propsTask.data?.form?.elements]);

  const nested = props.elements && props.elements.length > 0;
  const padDescription =
    props.view === TaskViews.CONDENSED &&
    !!props.task.description &&
    props.task.description.length > 0;

  const stepIndexes = useMemo(() => {
    const steps: number[][] = [];
    let currentStepIndexes: number[] = [];

    trueElements.forEach((element, index) => {
      if (
        element.type === FormElementType.Heading &&
        currentStepIndexes.length > 0
      ) {
        steps.push(currentStepIndexes);
        currentStepIndexes = [];
      }
      currentStepIndexes.push(index);
    });

    if (currentStepIndexes.length > 0) {
      steps.push(currentStepIndexes);
    }

    return steps;
  }, [trueElements]);

  const [disabled, setDisabled] = useState<boolean>(
    !(editing ?? !props.isDisabled),
  );

  const [validationIndexes, setValidationIndexes] = useState<Array<number>>(
    propsExecution?.data?.wizardData?.validationIndexes || [...stepIndexes[0]],
  );

  useEffect(() => {
    setDisabled(!(editing ?? !props.isDisabled));
  }, [editing, props.isDisabled, props.taskExecution]);

  const [currentStep, setCurrentStep] = useState(
    propsExecution?.data?.wizardData?.currentStep || 0,
  );

  const saveExecutionData = useCallback(
    async (
      step?: number,
      saveOnly?: boolean,
      updatedValidationIndexes?: Array<number>,
    ) => {
      if (!saveOnly) saveOnly = false;
      try {
        const execution = {
          ...propsExecution,
          task_id: propsTask.id,
          state: TaskExecutionState.Completed,
          data: {
            ...executeWizardFormTask(
              propsTask,
              updatedValidationIndexes || [],
              saveOnly,
            ).data,
            wizardData: {
              ...propsExecution?.data?.wizardData,
              ...(step !== undefined && { currentStep: step }),
              validationIndexes: updatedValidationIndexes ?? validationIndexes,
            },
          },
        } as TaskFormExecution;

        if (onExecute) {
          await onExecute(execution, !saveOnly).then(() => {
            anim?.attemptToExecuteAnimation();
          });
        } else {
          await executeTask(execution, !saveOnly).then(() => {
            anim?.attemptToExecuteAnimation();
          });
        }

        if (!saveOnly) {
          setEditing(false);
        }
      } catch (err: unknown) {
        error(String(err));
      }
    },
    [
      anim,
      error,
      executeTask,
      onExecute,
      propsExecution,
      propsTask,
      setEditing,
      validationIndexes,
    ],
  );

  const gotoStep = useCallback(
    (newStep: number, ignoreValidationSteps?: boolean) => {
      if (newStep >= 0 && newStep < stepIndexes.length) {
        setCurrentStep(newStep);

        if (newStep > currentStep) {
          const nextStepIndexes = stepIndexes[newStep] || [];
          if (ignoreValidationSteps != true)
            setValidationIndexes((prev) => {
              const updatedIndexes = [
                ...prev,
                ...nextStepIndexes.filter((idx) => !prev.includes(idx)),
              ];
              saveExecutionData(newStep, true, updatedIndexes);
              setReviewMode(false);
              return updatedIndexes;
            });
        } else if (newStep < currentStep) {
          const allowedIndexes = stepIndexes.slice(0, newStep + 1).flat();
          if (ignoreValidationSteps != true)
            setValidationIndexes((prev) => {
              const updatedIndexes = prev.filter((idx) =>
                allowedIndexes.includes(idx),
              );
              saveExecutionData(newStep, true, updatedIndexes);
              setReviewMode(false);
              return updatedIndexes;
            });
        }
      }
    },
    [currentStep, saveExecutionData, stepIndexes],
  );

  const validateElements = useCallback(
    (validateAll?: boolean) => {
      const indexesToValidate = validateAll
        ? validationIndexes
        : stepIndexes[currentStep] || [];
      for (const index of indexesToValidate) {
        const element = validateAll
          ? propsExecution?.data?.form?.elements[index]
          : trueElements[index];
        if (!element) continue;

        if (!element.required) {
          continue;
        }

        switch (element.type) {
          case FormElementType.Address:
            if (
              element.value === undefined ||
              !validateAddress(element.value)
            ) {
              return false;
            }
            break;
          case FormElementType.Text:
          case FormElementType.Date:
          case FormElementType.Upload:
            if (!element.value || element.value === "") {
              return false;
            }
            break;
          case FormElementType.Radio:
          case FormElementType.Selection:
          case FormElementType.MultiSelection:
            if (
              !element.selection ||
              (Array.isArray(element.selection) &&
                element.selection.length === 0)
            ) {
              return false;
            }
            break;
          case FormElementType.Checkbox:
            if (element.value === undefined) {
              return false;
            }
            break;
          case FormElementType.FieldDefinition:
            if (!element.value || element.value.length === 0) {
              return false;
            }
            break;
          default:
            break;
        }
      }
      return true;
    },
    [
      currentStep,
      propsExecution?.data?.form?.elements,
      stepIndexes,
      trueElements,
      validationIndexes,
    ],
  );

  const gotoPreviousStep = useCallback(() => {
    // This calculates the previous step based on the
    // indexes that were previously accessed
    const indexesToRemove = stepIndexes[currentStep];
    const newValidationIndexes = validationIndexes.filter(
      (index) => !indexesToRemove.includes(index),
    );
    const newStep = stepIndexes.findIndex((step) =>
      step.includes(newValidationIndexes[newValidationIndexes.length - 1]),
    );
    setValidationIndexes(newValidationIndexes);
    gotoStep(newStep);
  }, [currentStep, gotoStep, stepIndexes, validationIndexes]);

  const handleButtonGroupAction = useCallback(
    (settings: ButtonGroupSettings, noValidate?: boolean) => {
      switch (settings.assignment) {
        case ButtonGroupFunction.NEXT_STEP: {
          const isValid = noValidate ? true : validateElements();
          if (isValid) {
            gotoStep(currentStep + 1);
          } else {
            error(STR_PLEASE_COMPLETE);
          }
          break;
        }
        case ButtonGroupFunction.PREV_STEP:
          gotoPreviousStep();
          break;

        case ButtonGroupFunction.SUBMIT_TASK:
          props.task?.data?.confirmation
            ? setOpenConfirmationModal(true)
            : saveExecutionData(0, false, validationIndexes);
          break;

        case ButtonGroupFunction.GOTO_STEP: {
          const isValid = noValidate ? true : validateElements();
          if (isValid) {
            if (typeof settings.data === "number" && !isNaN(settings.data)) {
              gotoStep(settings.data);
            } else {
              error(STR_INVALID_STEP);
            }
          } else {
            error(STR_PLEASE_COMPLETE);
          }
          break;
        }
      }
    },
    [
      validateElements,
      gotoPreviousStep,
      props.task?.data?.confirmation,
      saveExecutionData,
      validationIndexes,
      gotoStep,
      currentStep,
      error,
    ],
  );

  const autofillElements = async (
    autofill: Record<string, unknown>,
    assignment?: {
      action?: ButtonGroupFunction;
      data?: number;
    },
  ) => {
    if (!Array.isArray(trueElements)) return;

    for (const element of trueElements) {
      const updates = autofill[element.id];
      if (updates && typeof updates === "object") {
        // Special cases switch
        switch (element.type) {
          // I despise this
          case FormElementType.FieldDefinition: {
            console.log(autofill);
            const fetchedFieldDefinition = element.field_definition_id
              ? await FieldDefinitionAPI.get(element.field_definition_id)
              : undefined;
            if (!fetchedFieldDefinition) break;
            // Always use the fetched definition as the base
            const baseElements = fetchedFieldDefinition.data.elements;
            // Only update if updates.value is an array
            if (Array.isArray((updates as { value?: unknown }).value)) {
              const updateArr = (
                updates as { value: Array<FormDefinitionType> }
              ).value;

              // Merge updates by id, keep all base elements
              const merged = baseElements.map((child) => {
                const updated = updateArr.find((u) => u.id === child.id);
                return updated ? { ...child, ...updated } : child;
              });
              updateArr.forEach((update) => {
                if (!baseElements.some((child) => child.id === update.id)) {
                  merged.push(update);
                }
              });

              element.value = merged as typeof element.value;
            } else {
              element.value = baseElements;
            }
            break;
          }
          default:
            Object.assign(element, updates);
            break;
        }
      }
    }

    // Update validated indexes
    const newValidationIndexes = [...validationIndexes];
    const autofilledIds = Object.keys(autofill);

    const targetStep =
      assignment?.action === ButtonGroupFunction.NEXT_STEP
        ? currentStep + 1
        : assignment?.action === ButtonGroupFunction.GOTO_STEP
          ? (assignment.data ?? currentStep)
          : currentStep;

    if (targetStep >= currentStep)
      autofilledIds.forEach((id) => {
        const elementIndex = trueElements.findIndex((el) => el.id === id);
        if (elementIndex !== -1) {
          const stepIdx = stepIndexes.findIndex((step) =>
            step.includes(elementIndex),
          );
          if (stepIdx !== -1) {
            const stepElements = stepIndexes[stepIdx];
            stepElements.forEach((idx) => {
              if (
                !newValidationIndexes.includes(idx) &&
                stepIdx <= targetStep
              ) {
                newValidationIndexes.push(idx);
              }
            });
          }
        }
      });

    setValidationIndexes(newValidationIndexes);
    //saveExecutionData(undefined, true, newValidationIndexes);

    if (assignment && assignment.action) {
      handleButtonGroupAction(
        {
          assignment: assignment.action,
          data: assignment.data,
        } as ButtonGroupSettings,
        true,
      );
    }
  };

  const contents = useMemo(
    () =>
      trueElements.map((element, index) => {
        const formElementExecution = (
          props.executionElements ??
          propsExecution?.data?.form?.elements ??
          []
        ).find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (e: any) => e.id == element.id,
        );

        let elem;
        elem = getCommonFormExecutionComponent({
          element: element,
          trueElements: trueElements,
          index: index,
          state: propsExecution?.state,
          formElementExecution: formElementExecution,
          disabled: disabled,
          nested: nested,
          taskExecution: props.taskExecution,
          task: props.task,
          validElements: validationIndexes,
        });

        if (!elem)
          switch (element.type) {
            // Wizard Specific Elements:
            case FormElementType.ButtonGroup:
              if (reviewMode) {
                elem = <></>;
                return;
              }
              elem = (
                <TaskButtonGroupFormComponent
                  {...(element as ButtonGroupFormElement)}
                  execution={formElementExecution}
                  disabled={disabled}
                  disableType={{
                    SUBMIT: !validateElements(true),
                  }}
                  key={element.id}
                  onClick={handleButtonGroupAction}
                />
              );
              break;
            case FormElementType.Typography:
              elem = (
                <Typography
                  sx={{
                    py: 1,
                  }}
                  variant={
                    (element as TypographyFormElement).variant || "body1"
                  }
                  textAlign={
                    (element as TypographyFormElement).textAlign || "left"
                  }
                >
                  {formElementExecution?.label}
                </Typography>
              );
              break;
            case FormElementType.Summary:
              // Only render summary if its index is in the current step
              if (propsExecution && stepIndexes[currentStep]?.includes(index)) {
                elem = (
                  <TaskSummaryFormComponent
                    disabled={disabled}
                    execution={propsExecution}
                    onGoToStep={(num) => {
                      // Set validation indexes for the selected step
                      const stepIdxs = stepIndexes[num] || [];
                      setValidationIndexes((prev) => {
                        const merged = [...prev];
                        stepIdxs.forEach((idx) => {
                          if (!merged.includes(idx)) merged.push(idx);
                        });
                        return merged;
                      });
                      setReturnTo(currentStep);
                      setReviewMode(true);
                      gotoStep(num, true);
                    }}
                  />
                );
              } else return;
              break;
            case FormElementType.CopilotAutofill:
              return (
                <CopilotAutofillFormComponent
                  {...(element as CopilotAutofillElement)}
                  execution={formElementExecution}
                  taskExecution={props.taskExecution}
                  onAutofillComplete={autofillElements}
                />
              );
            default:
              return;
          }

        if (!elem)
          console.warn(
            `Unable to map element of type ${element.type} to a corresponding JSX element in the Wizard renderer.`,
          );

        return elem;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      trueElements,
      props.executionElements,
      props.task,
      propsExecution,
      disabled,
      nested,
      //validationIndexes,
      handleButtonGroupAction,
      gotoStep,
    ],
  );

  const content = (
    <Grid
      container
      rowSpacing={props.isDisabled || !editing ? 1 : 2}
      direction="column"
      alignContent="stretch"
      justifyContent="center"
      pt={padDescription ? 2 : 0}
      pl={nested ? 3 : 0}
      width={"100%"}
      minWidth={"35vw"}
      sx={{
        pt: 2,
        "@media print": {
          paddingBottom: "64px", // Make all content visible for printing
        },
      }}
    >
      {reviewMode && (
        <Box
          width={"100%"}
          sx={() => ({
            display: "flex",
            mb: 1,
          })}
        >
          <Box sx={{ flexGrow: 1, textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  textAlign: "left",
                }}
              >
                <Typography variant={"body1"}>{STR_EDITING_STEP}</Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Delete />}
                onClick={() => {
                  // Remove all validation indexes of the current step
                  const stepIdxs = stepIndexes[currentStep] || [];
                  setValidationIndexes((prev) =>
                    prev.filter((idx) => !stepIdxs.includes(idx)),
                  );
                  gotoStep(returnTo);
                }}
              >
                {STR_CLEAR}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <Box
        sx={
          !reviewMode
            ? {}
            : (t) => ({
                px: 3,
                pt: 1.5,
                pb: 3,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 3,
              })
        }
      >
        {trueElements.map((_, index) => {
          const isVisible = stepIndexes[currentStep]?.includes(index);
          return (
            <Box
              key={index}
              sx={{
                visibility: isVisible ? "visible" : "hidden",
                opacity: isVisible ? 1 : 0,
                transition:
                  "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
                maxHeight: isVisible ? "500px" : "0px",
                overflow: "hidden",
              }}
            >
              {contents[index]}
            </Box>
          );
        })}
      </Box>
      {reviewMode && (
        <Box
          width={"100%"}
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 3,
          }}
        >
          <Button
            onClick={() => {
              gotoStep(returnTo);
            }}
            variant="contained"
          >
            {STR_SAVE_RETURN}
          </Button>
        </Box>
      )}
      <ConfirmationModal
        executeModal={() => {
          saveExecutionData(0, false);
        }}
        openModal={openConfirmationModal}
        confirmationData={props.task?.data?.confirmation}
        handleCloseModal={() => {
          setOpenConfirmationModal(false);
        }}
      />
    </Grid>
  );

  return children({ content });
};

export default WizardFormExecutionComponent;
